/** Pure bark pickers — messageKey only (no t() / Phaser). */

import { raidCatalog, type RaidClassId } from "../../data/raid-catalog";
import type { RaidUnit } from "./raid-types";

export type RaidBarkOrderId = "dash" | "groupDash" | "pin";
export type RaidBossBarkMoment = "telegraph" | "break" | "phase" | "leap";
export type RaidBruteBarkMoment = "spawn" | "telegraph" | "leap" | "break";
export type RaidAllyBarkKind = "deathReact" | "bigHit";

type BarkBucket = Record<string, readonly string[]>;
type BarkByClass = Partial<Record<RaidClassId, BarkBucket>>;
type BarkTable = Partial<Record<RaidBarkOrderId | RaidAllyBarkKind, BarkByClass>>;

type BossBarkTable = {
  chance?: number;
  cooldownSec?: number;
  telegraph?: readonly string[];
  break?: readonly string[];
  phase?: readonly string[];
  leap?: readonly string[];
};

type AddBarkTable = {
  minCount?: number;
  chance?: number;
  wave?: readonly string[];
  /** Assault drip chunk bark (occasional; shared encounter CD). */
  assaultChunkMinCount?: number;
  assaultChunkChance?: number;
  assaultChunkCooldownSec?: number;
  /** Mid-wave thrall combat bark (shared encounter CD). */
  combatChance?: number;
  combatCooldownSec?: number;
  combat?: readonly string[];
};

type BruteBarkTable = {
  chance?: number;
  cooldownSec?: number;
  /** Spawn pack bark chance (default higher than skill chance). */
  spawnChance?: number;
  spawn?: readonly string[];
  telegraph?: readonly string[];
  leap?: readonly string[];
  break?: readonly string[];
};

type BigHitBarkTable = {
  /** Hit damage ≥ maxHp * this fraction triggers (default 0.28). */
  hpFrac?: number;
  /** Absolute floor — whichever is lower with frac threshold wins as gate. */
  minDamage?: number;
  chance?: number;
  cooldownSec?: number;
};

type ExtendedBarks = BarkTable & {
  boss?: BossBarkTable;
  add?: AddBarkTable;
  brute?: BruteBarkTable;
  bigHitGate?: BigHitBarkTable;
};

function barksRoot(): ExtendedBarks {
  return raidCatalog.barks as ExtendedBarks;
}

function pickClassQuirkLine(
  kind: RaidBarkOrderId | RaidAllyBarkKind,
  classId: RaidClassId,
  quirkId: string,
  salt: number,
  fallbackKey: string,
): string {
  const byOrder = barksRoot()[kind] ?? {};
  const byClass = byOrder[classId] ?? {};
  const lines =
    byClass[quirkId] ??
    byClass.default ??
    byOrder.tank?.default ??
    byOrder.dps?.default ??
    byOrder.healer?.default ??
    [];
  if (lines.length === 0) return fallbackKey;
  const i = Math.abs(salt) % lines.length;
  return lines[i]!;
}

/**
 * Resolve a string key for a unit reacting to a commander gesture.
 * Fallback chain: quirk → class default → any class default under order.
 */
export function pickOrderBarkKey(
  orderId: RaidBarkOrderId,
  classId: RaidClassId,
  quirkId: string,
  salt = 0,
): string {
  return pickClassQuirkLine(
    orderId,
    classId,
    quirkId,
    salt,
    `raid.bark.${orderId}.fallback`,
  );
}

export function pickDeathReactBarkKey(
  classId: RaidClassId,
  quirkId: string,
  salt = 0,
): string {
  return pickClassQuirkLine(
    "deathReact",
    classId,
    quirkId,
    salt,
    "raid.bark.deathReact.fallback",
  );
}

export function pickBigHitBarkKey(
  classId: RaidClassId,
  quirkId: string,
  salt = 0,
): string {
  return pickClassQuirkLine(
    "bigHit",
    classId,
    quirkId,
    salt,
    "raid.bark.bigHit.fallback",
  );
}

export function bossBarkChance(): number {
  const c = Number(barksRoot().boss?.chance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.35;
}

export function bossBarkCooldownSec(): number {
  const c = Number(barksRoot().boss?.cooldownSec);
  return Number.isFinite(c) && c >= 0 ? c : 8;
}

export function addWaveBarkMinCount(): number {
  const n = Number(barksRoot().add?.minCount);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 2;
}

export function addWaveBarkChance(): number {
  const c = Number(barksRoot().add?.chance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 1;
}

export function bigHitBarkChance(): number {
  const c = Number(barksRoot().bigHitGate?.chance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.55;
}

export function bigHitBarkCooldownSec(): number {
  const c = Number(barksRoot().bigHitGate?.cooldownSec);
  return Number.isFinite(c) && c >= 0 ? c : 4;
}

/** Damage must meet max(minDamage, maxHp * hpFrac) to qualify. */
export function bigHitDamageThreshold(maxHp: number): number {
  const gate = barksRoot().bigHitGate ?? {};
  const frac = Number(gate.hpFrac);
  const minD = Number(gate.minDamage);
  const byFrac =
    Number.isFinite(frac) && frac > 0 ? maxHp * frac : maxHp * 0.28;
  const floor = Number.isFinite(minD) && minD > 0 ? minD : 18;
  return Math.max(floor, byFrac);
}

/** Gate: cooldown clear + roll < chance. Pure — inject roll for tests. */
export function rollBarkGate(
  chance: number,
  cooldownLeft: number,
  roll: number,
): boolean {
  if (cooldownLeft > 1e-6) return false;
  if (chance <= 0) return false;
  if (chance >= 1) return true;
  return roll < chance;
}

export function pickBossBarkKey(
  moment: RaidBossBarkMoment,
  salt = 0,
): string {
  const boss = barksRoot().boss ?? {};
  const lines = boss[moment] ?? boss.telegraph ?? [];
  if (lines.length === 0) {
    return `raid.bark.boss.${moment}.fallback`;
  }
  const i = Math.abs(salt) % lines.length;
  return lines[i]!;
}

export function pickAddWaveBarkKey(salt = 0): string {
  const lines = barksRoot().add?.wave ?? [];
  if (lines.length === 0) {
    return "raid.bark.add.wave.fallback";
  }
  const i = Math.abs(salt) % lines.length;
  return lines[i]!;
}

export function assaultChunkBarkMinCount(): number {
  const n = Number(barksRoot().add?.assaultChunkMinCount);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

export function assaultChunkBarkChance(): number {
  const c = Number(barksRoot().add?.assaultChunkChance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.38;
}

export function assaultChunkBarkCooldownSec(): number {
  const c = Number(barksRoot().add?.assaultChunkCooldownSec);
  return Number.isFinite(c) && c >= 0 ? c : 3.5;
}

export function addCombatBarkChance(): number {
  const c = Number(barksRoot().add?.combatChance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.1;
}

export function addCombatBarkCooldownSec(): number {
  const c = Number(barksRoot().add?.combatCooldownSec);
  return Number.isFinite(c) && c >= 0 ? c : 7;
}

export function pickAddCombatBarkKey(salt = 0): string {
  const lines = barksRoot().add?.combat ?? barksRoot().add?.wave ?? [];
  if (lines.length === 0) {
    return "raid.bark.add.combat.fallback";
  }
  const i = Math.abs(salt) % lines.length;
  return lines[i]!;
}

export function bruteBarkChance(): number {
  const c = Number(barksRoot().brute?.chance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.5;
}

export function bruteBarkSpawnChance(): number {
  const c = Number(barksRoot().brute?.spawnChance);
  return Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.9;
}

export function bruteBarkCooldownSec(): number {
  const c = Number(barksRoot().brute?.cooldownSec);
  return Number.isFinite(c) && c >= 0 ? c : 5.5;
}

export function pickBruteBarkKey(
  moment: RaidBruteBarkMoment,
  salt = 0,
): string {
  const brute = barksRoot().brute ?? {};
  const lines = brute[moment] ?? brute.spawn ?? [];
  if (lines.length === 0) {
    return `raid.bark.brute.${moment}.fallback`;
  }
  const i = Math.abs(salt) % lines.length;
  return lines[i]!;
}

/**
 * One speaker for groupDash: prefer tank, else closest to destination,
 * else lowest unitId.
 */
export function pickGroupDashSpeaker(
  units: readonly RaidUnit[],
  tx: number,
  ty: number,
): RaidUnit | null {
  if (units.length === 0) return null;
  const tanks = units.filter((u) => u.classId === "tank");
  const pool = tanks.length > 0 ? tanks : units;
  let best = pool[0]!;
  let bestDist = Infinity;
  for (const u of pool) {
    const d = Math.hypot(u.x - tx, u.y - ty);
    if (
      d < bestDist - 1e-6 ||
      (Math.abs(d - bestDist) <= 1e-6 && u.id < best.id)
    ) {
      best = u;
      bestDist = d;
    }
  }
  return best;
}

/** Closest living ally to a point (death reaction speaker). */
export function pickDeathReactSpeaker(
  living: readonly RaidUnit[],
  x: number,
  y: number,
): RaidUnit | null {
  if (living.length === 0) return null;
  let best = living[0]!;
  let bestDist = Infinity;
  for (const u of living) {
    const d = Math.hypot(u.x - x, u.y - y);
    if (
      d < bestDist - 1e-6 ||
      (Math.abs(d - bestDist) <= 1e-6 && u.id < best.id)
    ) {
      best = u;
      bestDist = d;
    }
  }
  return best;
}

/** Pick one thrall from a spawn batch (stable salt → unit id). */
export function pickAddWaveSpeaker(
  spawned: readonly RaidUnit[],
  salt = 0,
): RaidUnit | null {
  if (spawned.length === 0) return null;
  const i = Math.abs(salt) % spawned.length;
  return spawned[i] ?? spawned[0] ?? null;
}
